import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { useAuth } from '../context/AuthContext';
import { useVideo } from '../context/VideoContext';
import VideoList from '../components/admin/VideoList';
import CompanyManagement from '../components/admin/CompanyManagement';
import VideoForm from '../components/admin/VideoForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiVideo, FiLogOut, FiHome, FiBriefcase, FiList, FiPlus, FiUploadCloud, FiZap, FiCheckCircle, FiAlertCircle, FiLoader } = FiIcons;

const ENRICH_WEBHOOK = 'https://n8n.reesgargi.com/webhook/1b055971-577e-4ec3-aa36-8e078b291190';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [editingVideo, setEditingVideo] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [enrichUrls, setEnrichUrls] = useState('');
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichToast, setEnrichToast] = useState(null);

  const { user, role, logout, loading: authLoading } = useAuth();
  const { videos, companies } = useVideo();
  const navigate = useNavigate();
  const dashboardRef = useRef(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) navigate('/user');
      else if (role !== 'admin') navigate('/');
    }
  }, [user, role, authLoading, navigate]);

  useEffect(() => {
    let ctx;
    if (dashboardRef.current) {
      ctx = gsap.context(() => {
        gsap.fromTo(dashboardRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" });
      }, dashboardRef);
    }
    return () => ctx && ctx.revert();
  }, [authLoading]);

  useEffect(() => {
    if (!enrichToast) return;
    const timer = setTimeout(() => setEnrichToast(null), 4000);
    return () => clearTimeout(timer);
  }, [enrichToast]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAddVideo = () => {
    setEditingVideo(null);
    setShowForm(true);
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingVideo(null);
  };

  const detectVideoInfo = (url) => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i;
    const ytMatch = trimmed.match(youtubeRegex);
    if (ytMatch) return { platform: 'youtube', id: ytMatch[1] };
    const vimeoMatch = trimmed.match(vimeoRegex);
    if (vimeoMatch) return { platform: 'vimeo', id: vimeoMatch[1] };
    return null;
  };

  const handleEnrichVideos = async () => {
    const lines = enrichUrls.split('\n');
    const parsed = lines.map(detectVideoInfo).filter(Boolean);

    if (parsed.length === 0) {
      setEnrichToast({ type: 'error', message: 'No valid YouTube or Vimeo URLs found. Please check your input.' });
      return;
    }

    setIsEnriching(true);
    setEnrichToast(null);

    try {
      const response = await fetch(ENRICH_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });

      if (!response.ok) throw new Error(`Webhook responded with status ${response.status}`);

      setEnrichToast({ type: 'success', message: `Successfully sent ${parsed.length} video${parsed.length > 1 ? 's' : ''} for enrichment.` });
      setEnrichUrls('');
    } catch (err) {
      console.error('Enrich error:', err);
      setEnrichToast({ type: 'error', message: err.message || 'Failed to send videos for enrichment. Please try again.' });
    } finally {
      setIsEnriching(false);
    }
  };

  const stats = [
    { label: 'Live Videos', value: videos.length, icon: FiVideo, color: 'text-green-400' },
    { label: 'Companies', value: companies.length, icon: FiBriefcase, color: 'text-blue-400' },
  ];

  if (authLoading || role !== 'admin') return null;

  return (
    <div ref={dashboardRef} className="min-h-screen p-6 bg-[#09090b]">
      <div className="max-w-7xl mx-auto">
        <div className="glass-card p-6 mb-8 border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight">Admin <span className="text-orange-500">Suite</span></h1>
              <p className="text-slate-400 text-sm mt-1">Logged in as {user.email}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => navigate('/')} className="btn-secondary flex items-center gap-2"><SafeIcon icon={FiHome} /> Site View</button>
              <button onClick={handleLogout} className="btn-secondary flex items-center gap-2 border-red-500/10 hover:border-red-500/50 hover:text-red-400">
                <SafeIcon icon={FiLogOut} /> Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {stats.map((stat, idx) => (
            <motion.div key={idx} className="glass-card p-6 flex items-center gap-5 border-white/5" whileHover={{ y: -5 }}>
              <div className={`p-4 rounded-2xl bg-white/5 ${stat.color}`}><SafeIcon icon={stat.icon} className="text-2xl" /></div>
              <div>
                <div className="text-3xl font-bold text-white font-mono">{stat.value}</div>
                <div className="text-slate-500 text-xs font-bold uppercase tracking-widest">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 bg-zinc-900/50 p-1.5 rounded-2xl w-fit border border-white/5 overflow-x-auto">
          <TabButton active={activeTab === 'videos'} onClick={() => { setActiveTab('videos'); setShowForm(false); }} icon={FiList} label="Live Videos" />
          <TabButton active={activeTab === 'companies'} onClick={() => { setActiveTab('companies'); setShowForm(false); }} icon={FiBriefcase} label="Directory" />
          <TabButton active={activeTab === 'enrich'} onClick={() => { setActiveTab('enrich'); setShowForm(false); }} icon={FiUploadCloud} label="Bulk Upload" />
        </div>

        <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          {activeTab === 'videos' && (
            <div className="space-y-6">
              <div className="glass-card p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Video Management</h2>
                    <p className="text-white/80">Add, edit, and manage your video collection</p>
                  </div>
                  <button onClick={handleAddVideo} className="btn-primary flex items-center gap-2">
                    <SafeIcon icon={FiPlus} /> Add New Video
                  </button>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {showForm ? (
                  <motion.div key="form" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                    <VideoForm video={editingVideo} onClose={handleCloseForm} />
                  </motion.div>
                ) : (
                  <motion.div key="list" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}>
                    <VideoList onEdit={handleEditVideo} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {activeTab === 'companies' && <CompanyManagement />}

          {activeTab === 'enrich' && (
            <div className="glass-card p-6 border-white/5 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
                  <SafeIcon icon={FiUploadCloud} className="text-orange-500" />
                  Bulk Upload
                </h2>
                <p className="text-slate-400 text-sm">Paste YouTube or Vimeo URLs below (one per line). Each will be parsed and sent to the enrichment pipeline.</p>
              </div>

              <textarea
                value={enrichUrls}
                onChange={(e) => setEnrichUrls(e.target.value)}
                rows={10}
                disabled={isEnriching}
                placeholder={"Paste YouTube or Vimeo URLs (one per line)...\n\nhttps://www.youtube.com/watch?v=dQw4w9WgXcQ\nhttps://vimeo.com/123456789"}
                className="w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-2xl text-white placeholder-white/20 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none resize-none font-mono text-sm disabled:opacity-50"
              />

              <AnimatePresence>
                {enrichToast && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className={`flex items-start gap-3 p-4 rounded-2xl border text-sm font-medium ${
                      enrichToast.type === 'success'
                        ? 'bg-green-500/10 border-green-500/20 text-green-400'
                        : 'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}
                  >
                    <SafeIcon
                      icon={enrichToast.type === 'success' ? FiCheckCircle : FiAlertCircle}
                      className="text-lg mt-0.5 shrink-0"
                    />
                    {enrichToast.message}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-end">
                <button
                  onClick={handleEnrichVideos}
                  disabled={isEnriching || !enrichUrls.trim()}
                  className="btn-primary flex items-center gap-2 px-8 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEnriching ? (
                    <><SafeIcon icon={FiLoader} className="animate-spin" /> Uploading...</>
                  ) : (
                    <><SafeIcon icon={FiUploadCloud} /> Upload Videos</>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${active ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'text-slate-500 hover:text-white'}`}>
    <SafeIcon icon={icon} /> {label}
  </button>
);

export default AdminDashboard;